import type {
  IContentPageFields,
  INavigation,
  LOCALE_CODE,
  IContentPage,
  IProductEnrichmentFields,
  EntryWithLinkData
} from '~~/lib/ContentfulService';
import type { ResolvedEntryTypes } from '~/utils/resolveEntry';
import type { MinifiedSecondaryNavigation } from '~/types/MinifiedSecondaryNavigation';
import type { Sys } from 'contentful';
import { transformContentfulSys } from './helper/transformContentfulSys';
import type { SerializableRecord } from './model';
import { transformCircularContentfulReference } from './helper/transformCircularContentfulReference';
import {
  type ContentfulFile,
  transformContentfulFile
} from './helper/transformContentfulFile';
import { isEntry } from '~/utils/guards/isEntry';
import { isSerializableRecord } from './helper/isSerializableRecord';
import { isRichTextEntryNodeWithoutTarget } from './helper/isRichTextEntryNodeWithoutTarget';
import { transformInternalAnchorReference } from './helper/transformInternalAnchorReference';
import { getContentTypeId } from './helper/getContentTypeId';
import { compactLinkReference } from './helper/compactLinkReference';
import { compactPayloadEntrySys } from './helper/compactPayloadEntrySys';
import {
  compactResolvablePageReference,
  isResolvablePageContentType,
  resolvePageReferencePurpose
} from './helper/compactResolvablePageReference';
import {
  extractMinifiedSecondaryNavigationFromPage as extractMinifiedSecondaryNavigationFromPageImpl,
  minifySecondaryNavigation as minifySecondaryNavigationImpl
} from './helper/minifySecondaryNavigation';
import { extractPageTeaserMetaFromRootPage as extractPageTeaserMetaFromRootPageImpl } from './helper/extractPageTeaserMeta';
import type { PageTeaserMeta } from '~/types/PageTeaserMeta';
import { isEntryEnabledInLocale } from '~/utils/guards/isEntryEnabledInLocale';

export class ContentfulTransformerService {
  /**
   * Fields to remove from every entry.
   */
  private readonly contentfulFieldsToRemove = new Set([
    'internalName',
    'locales'
  ]);

  /**
   * Fields to remove from the root contentful page.
   */
  private readonly contentPageFieldsToRemove = new Set<
    keyof IContentPageFields
  >(['boostPage', 'includePageInSitemap', 'internalSearch']);

  /**
   * Enrichment fields that are only needed server-side for rule matching.
   * `rules` can contain thousands of SKUs. `sortingPrio` stays in the payload
   * because the PDP groups enrichments into tabs by it (DBP-4500).
   */
  private readonly productEnrichmentFieldsToRemove = new Set<
    keyof IProductEnrichmentFields
  >(['rules']);

  /**
   * Layout fields only needed when an image entry is rendered via ContentfulImage
   * (contentSection / callToAction / Rich-Text-Embed target), not in teaser/card contexts.
   */
  private readonly imageLayoutFieldsToRemove = new Set([
    'imageWidth',
    'imageCentered',
    'imageCenteredInRow',
    'imageFormat'
  ]);

  private readonly fullRenderImageParentFieldKeys = new Set([
    'contentSection',
    'callToAction',
    'target'
  ]);

  public minifySecondaryNavigation(
    navigation: INavigation | null | undefined
  ): MinifiedSecondaryNavigation | undefined {
    return minifySecondaryNavigationImpl(navigation);
  }

  public extractMinifiedSecondaryNavigationFromPage(
    page: { fields?: { secondaryNavigation?: INavigation } } | null | undefined
  ): MinifiedSecondaryNavigation | undefined {
    return extractMinifiedSecondaryNavigationFromPageImpl(page);
  }

  public extractPageTeaserMetaFromRootPage(
    page: IContentPage | null | undefined
  ): PageTeaserMeta | undefined {
    return extractPageTeaserMetaFromRootPageImpl(page);
  }

  /**
   * Minifies a payload the client resolves itself (`resolveEntry`).
   *
   * The flat `linkedEntries` map carries no field context, so referenced pages
   * are compacted with the teaser field set — a superset of the link field set
   * ({@link compactResolvablePageReference}).
   */
  public sanitizeEntryWithLinkDataPayload<T extends ResolvedEntryTypes>(
    payload: EntryWithLinkData<T>,
    locale?: LOCALE_CODE
  ): EntryWithLinkData<T> {
    const linkedEntries: Record<string, ResolvedEntryTypes> = {};

    for (const [id, linkedEntry] of Object.entries(payload.linkedEntries)) {
      const sanitized = this.sanitizeLinkedEntry(linkedEntry, locale);

      if (sanitized) {
        linkedEntries[id] = sanitized;
      }
    }

    return {
      entry: this.sanitizeContentfulPayload(
        payload.entry,
        undefined,
        true,
        locale
      ) as T,
      linkedEntries,
      errors: payload.errors
    };
  }

  private sanitizeLinkedEntry(
    linkedEntry: ResolvedEntryTypes,
    locale?: LOCALE_CODE
  ): ResolvedEntryTypes | undefined {
    const source = linkedEntry as unknown as SerializableRecord;

    if (locale && isEntry(source) && !isEntryEnabledInLocale(source, locale)) {
      return undefined;
    }

    if (
      isEntry(source) &&
      isResolvablePageContentType(getContentTypeId(source))
    ) {
      const compactedPage = compactResolvablePageReference(source, 'teaser');

      if (compactedPage) {
        return compactedPage as unknown as ResolvedEntryTypes;
      }
    }

    // Linked entries are roots of their own graph here: their references are
    // still unresolved links the client looks up in `linkedEntries`.
    return this.sanitizeContentfulPayload(linkedEntry, undefined, true, locale);
  }

  public sanitizeContentfulPayload<T>(
    value: T,
    stack = new WeakSet<object>(),
    isRootEntry = false,
    locale?: LOCALE_CODE,
    parentFieldKey?: string,
    isCompactedPageReference = false
  ): T | undefined {
    if (!value || typeof value !== 'object') {
      return value;
    }

    if (stack.has(value)) {
      const circularReference = isSerializableRecord(value)
        ? transformCircularContentfulReference(value)
        : undefined;

      return circularReference as T;
    }

    if (Array.isArray(value)) {
      stack.add(value);
      const sanitizedArray = value
        .map((item) =>
          this.sanitizeContentfulPayload(
            item,
            stack,
            false,
            locale,
            parentFieldKey,
            isCompactedPageReference
          )
        )
        .filter((item) => item !== undefined);
      stack.delete(value);

      return sanitizedArray as T;
    }

    const source = value as SerializableRecord;
    if (
      locale &&
      !isRootEntry &&
      isEntry(source) &&
      !isEntryEnabledInLocale(source, locale)
    ) {
      return undefined;
    }

    if (
      !isRootEntry &&
      !isCompactedPageReference &&
      isEntry(source) &&
      isResolvablePageContentType(getContentTypeId(source))
    ) {
      const compactedPage = compactResolvablePageReference(
        source,
        resolvePageReferencePurpose(parentFieldKey)
      );

      if (compactedPage) {
        stack.add(value);
        const sanitizedPage = this.sanitizeContentfulPayload(
          compactedPage,
          stack,
          false,
          locale,
          parentFieldKey,
          true
        );
        stack.delete(value);

        return sanitizedPage as T;
      }
    }

    const sanitized: SerializableRecord = {};
    stack.add(value);

    for (const [key, fieldValue] of Object.entries(source)) {
      if (key === 'sys') {
        if (isCompactedPageReference) {
          const compactSys = compactPayloadEntrySys(fieldValue);
          if (compactSys) {
            sanitized[key] = compactSys;
            continue;
          }
        }

        // XXX: proper typing
        sanitized[key] = transformContentfulSys(fieldValue as any as Sys);
        continue;
      }

      if (key === 'metadata') {
        continue;
      }

      if (key === 'file' && isSerializableRecord(fieldValue)) {
        sanitized[key] = transformContentfulFile(fieldValue as ContentfulFile);
        continue;
      }

      if (key === 'externalLink' && isSerializableRecord(fieldValue)) {
        sanitized[key] =
          compactLinkReference(fieldValue) ??
          this.sanitizeContentfulPayload(fieldValue, stack, false, locale);
        continue;
      }

      if (key === 'internalAnchor' && isSerializableRecord(fieldValue)) {
        sanitized[key] =
          transformInternalAnchorReference(fieldValue) ??
          this.sanitizeContentfulPayload(fieldValue, stack, false, locale);
        continue;
      }

      const sanitizedValue = this.sanitizeContentfulPayload(
        fieldValue,
        stack,
        false,
        locale,
        key,
        isCompactedPageReference
      );

      if (sanitizedValue !== undefined) {
        sanitized[key] = sanitizedValue;
      }
    }

    const fields = isSerializableRecord(sanitized.fields)
      ? sanitized.fields
      : undefined;

    if (fields) {
      for (const field of this.contentfulFieldsToRemove) {
        delete fields[field];
      }
    }

    if (getContentTypeId(source) === 'contentPage' && fields) {
      for (const field of this.contentPageFieldsToRemove) {
        delete fields[field];
      }
    }

    if (getContentTypeId(source) === 'productEnrichment' && fields) {
      for (const field of this.productEnrichmentFieldsToRemove) {
        delete fields[field];
      }
    }

    if (
      getContentTypeId(source) === 'image' &&
      fields &&
      parentFieldKey &&
      !this.fullRenderImageParentFieldKeys.has(parentFieldKey)
    ) {
      for (const field of this.imageLayoutFieldsToRemove) {
        delete fields[field];
      }
    }

    stack.delete(value);

    if (isRichTextEntryNodeWithoutTarget(sanitized)) {
      return undefined;
    }

    return sanitized as T;
  }
}
